// Route table for /patient-home/*. Mirrors the IA in
// docs/PATIENT_PORTAL_PLAN.md §4.1. Phase F1 ships placeholders for every
// route — subsequent phases swap each Element for the real page.
//
// Path strings are RELATIVE to /patient-home (parent route in App.jsx is
// `/patient-home/*` and React Router v6 matches descendant <Routes> against
// the splat).

import React from 'react'

const TodayPage = React.lazy(() => import('../pages/today/TodayPage'))

// Care
const CareIndexPage = React.lazy(() => import('../pages/care/CareIndexPage'))
const MedicinesPage = React.lazy(() => import('../pages/care/MedicinesPage'))
const MedicineDetailPage = React.lazy(() => import('../pages/care/MedicineDetailPage'))
const DietPage = React.lazy(() => import('../pages/care/DietPage'))
const ActivitiesPage = React.lazy(() => import('../pages/care/ActivitiesPage'))
const VitalsPage = React.lazy(() => import('../pages/care/VitalsPage'))
const GoalsPage = React.lazy(() => import('../pages/care/GoalsPage'))

// Assessments
const PatientAssessmentsPage = React.lazy(() =>
  import('../pages/assessments/PatientAssessmentsPage'),
)
const PatientAssessmentPlayerPage = React.lazy(() =>
  import('../pages/assessments/PatientAssessmentPlayerPage'),
)

// Sessions
const SessionsIndexPage = React.lazy(() => import('../pages/sessions/SessionsIndexPage'))
const UpcomingSessionsPage = React.lazy(() => import('../pages/sessions/UpcomingSessionsPage'))
const PastSessionsPage = React.lazy(() => import('../pages/sessions/PastSessionsPage'))
const SessionDetailPage = React.lazy(() => import('../pages/sessions/SessionDetailPage'))

// Practitioners
const PractitionersIndexPage = React.lazy(() =>
  import('../pages/practitioners/PractitionersIndexPage'),
)
const PractitionerDetailPage = React.lazy(() =>
  import('../pages/practitioners/PractitionerDetailPage'),
)

// Records
const RecordsIndexPage = React.lazy(() => import('../pages/records/RecordsIndexPage'))
const PrescriptionsPage = React.lazy(() => import('../pages/records/PrescriptionsPage'))
const NotesPage = React.lazy(() => import('../pages/records/NotesPage'))
const DocumentsPage = React.lazy(() => import('../pages/records/DocumentsPage'))
const SharedWithMePage = React.lazy(() => import('../pages/records/SharedWithMePage'))
const ReceiptsPage = React.lazy(() => import('../pages/records/ReceiptsPage'))

// Messages
const MessagesIndexPage = React.lazy(() => import('../pages/messages/MessagesIndexPage'))
const MessageThreadPage = React.lazy(() => import('../pages/messages/MessageThreadPage'))

// Shop
const ShopIndexPage = React.lazy(() => import('../pages/shop/ShopIndexPage'))
const PacksPage = React.lazy(() => import('../pages/shop/PacksPage'))
const SubscriptionPage = React.lazy(() => import('../pages/shop/SubscriptionPage'))
const DuesPage = React.lazy(() => import('../pages/shop/DuesPage'))

// Profile
const ProfileIndexPage = React.lazy(() => import('../pages/profile/ProfileIndexPage'))
const PersonalProfilePage = React.lazy(() => import('../pages/profile/PersonalProfilePage'))
const FamilyProfilesPage = React.lazy(() => import('../pages/profile/FamilyProfilesPage'))
const NotificationPrefsPage = React.lazy(() => import('../pages/profile/NotificationPrefsPage'))
const PrivacyPage = React.lazy(() => import('../pages/profile/PrivacyPage'))

// Notifications
const NotificationsCenterPage = React.lazy(() =>
  import('../pages/notifications/NotificationsCenterPage'),
)

// Help
const HelpIndexPage = React.lazy(() => import('../pages/help/HelpIndexPage'))
const CrisisPage = React.lazy(() => import('../pages/help/CrisisPage'))

const P = '/patient-home'

function r(relativePath, props = {}) {
  return {
    path: relativePath,
    fullPath: relativePath ? `${P}/${relativePath}` : P,
    ...props,
  }
}

export const patientRoutes = [
  r('', { exact: true, name: 'Today', Element: TodayPage }),

  r('care', { exact: true, name: 'Care', Element: CareIndexPage }),
  r('care/medicines', { exact: true, name: 'Medicines', Element: MedicinesPage }),
  r('care/medicines/:medicineId', { exact: true, name: 'Medicine', Element: MedicineDetailPage }),
  r('care/diet', { exact: true, name: 'Diet', Element: DietPage }),
  r('care/activities', { exact: true, name: 'Activities', Element: ActivitiesPage }),
  r('care/vitals', { exact: true, name: 'Vitals', Element: VitalsPage }),
  r('care/goals', { exact: true, name: 'Goals', Element: GoalsPage }),

  r('assessments', { exact: true, name: 'Assessments', Element: PatientAssessmentsPage }),
  r('assessments/:slug', {
    exact: true,
    name: 'Assessment',
    Element: PatientAssessmentPlayerPage,
  }),

  r('sessions', { exact: true, name: 'Sessions', Element: SessionsIndexPage }),
  r('sessions/upcoming', { exact: true, name: 'Upcoming sessions', Element: UpcomingSessionsPage }),
  r('sessions/past', { exact: true, name: 'Past sessions', Element: PastSessionsPage }),
  r('sessions/:eventId', { exact: true, name: 'Session', Element: SessionDetailPage }),

  r('practitioners', { exact: true, name: 'Practitioners', Element: PractitionersIndexPage }),
  r('practitioners/:practitionerId', {
    exact: true,
    name: 'Practitioner',
    Element: PractitionerDetailPage,
  }),

  r('records', { exact: true, name: 'Records', Element: RecordsIndexPage }),
  r('records/prescriptions', { exact: true, name: 'Prescriptions', Element: PrescriptionsPage }),
  r('records/notes', { exact: true, name: 'Notes', Element: NotesPage }),
  r('records/shared', { exact: true, name: 'Shared with me', Element: SharedWithMePage }),
  r('records/documents', { exact: true, name: 'Documents', Element: DocumentsPage }),
  r('records/receipts', { exact: true, name: 'Receipts', Element: ReceiptsPage }),

  r('messages', { exact: true, name: 'Messages', Element: MessagesIndexPage }),
  r('messages/:threadId', { exact: true, name: 'Conversation', Element: MessageThreadPage }),

  r('shop', { exact: true, name: 'Shop', Element: ShopIndexPage }),
  r('shop/packs', { exact: true, name: 'Packs', Element: PacksPage }),
  r('shop/subscription', { exact: true, name: 'Subscription', Element: SubscriptionPage }),
  r('shop/dues', { exact: true, name: 'Dues', Element: DuesPage }),

  r('profile', { exact: true, name: 'Profile', Element: ProfileIndexPage }),
  r('profile/personal', { exact: true, name: 'Personal profile', Element: PersonalProfilePage }),
  r('profile/family', { exact: true, name: 'Family', Element: FamilyProfilesPage }),
  r('profile/notifications', {
    exact: true,
    name: 'Notification preferences',
    Element: NotificationPrefsPage,
  }),
  r('profile/privacy', { exact: true, name: 'Privacy', Element: PrivacyPage }),

  r('notifications', {
    exact: true,
    name: 'Notifications',
    Element: NotificationsCenterPage,
  }),

  r('help', { exact: true, name: 'Help', Element: HelpIndexPage }),
  r('help/crisis', { exact: true, name: 'Crisis support', Element: CrisisPage }),
]
